import { TextField } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import React, { useCallback, useEffect, useState } from 'react';
import { debounce } from '../../../../../../../helpers/debounce';
import { ProspectSearchAutoCompleteFields } from '@zaplify/prospects';
import { apiEndpoint, getZaplifySdk } from '@zaplify/sdk';
import { CompanyAutocompleteOption } from '../../../../../../../types/option';
import { AutocompleteCompanyOption } from './autocomplete-company-option';
import { useMsal } from '@azure/msal-react';

const NO_MATCH_FOUND = 'No match found';

type Props = {
    suggestion: CompanyAutocompleteOption;
    idx: number;
    updateSelection: (newCompany: CompanyAutocompleteOption, idx: number) => void;
};

export const AutocompleteCompany = ({ idx, suggestion, updateSelection }: Props) => {
    const msal = useMsal();
    const sdk = getZaplifySdk(apiEndpoint, msal.instance);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<CompanyAutocompleteOption[]>([]);
    const [value, setValue] = useState<CompanyAutocompleteOption>({
        id: suggestion.id,
        name: suggestion.name || '',
        website: suggestion.website || '',
        industry: suggestion.industry || '',
        isValid: suggestion.isValid,
    });
    const [visibleValue, setVisibleValue] = useState(() => {
        return `${
            suggestion.website
                ? `${suggestion.name}\n${suggestion.website}`
                : suggestion.name
                ? suggestion.name
                : NO_MATCH_FOUND
        }`;
    });
    const [focused, setFocused] = useState(false);
    const loading = open && options.length === 0;

    useEffect(() => {
        let active = true;

        if (value.name === NO_MATCH_FOUND || !value || !value.name) {
            return;
        }

        if (!loading) {
            return undefined;
        }

        handleCompanyAutocomplete(value.name, active);

        return () => {
            active = false;
        };
    }, [loading, value]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    const handleValueChange = (newValue: CompanyAutocompleteOption) => {
        if (!newValue) {
            return;
        }
        const companyToUpdate = {
            id: newValue.id,
            name: newValue.name,
            domain: newValue.website,
            industry: newValue.industry,
            isValid: true,
        };
        setValue(companyToUpdate);
        setVisibleValue(`${newValue.name}\n${newValue.website}`);
        updateSelection(companyToUpdate, idx);
    };

    const handleCompanyAutocomplete = useCallback(
        debounce(async (nameInputValue: string, active: boolean) => {
            try {
                const rawCompanies: any = await getAutocompleteCompanies(nameInputValue);

                if (!rawCompanies) {
                    return;
                }

                if (active) {
                    const options = rawCompanies.map((c) => ({ name: c.name, domain: c.domain, industry: c.industry }));

                    setValue((v) => ({ ...v, isValid: true }));
                    setOptions(options);
                }
            } catch (err) {
                console.log((err as any).message);
            }
        }, 300),
        []
    );

    const getAutocompleteCompanies = async (companyQuery) =>
        await sdk.profiles.sources.autocomplete({
            field: ProspectSearchAutoCompleteFields.JOB_COMPANY_NAME,
            query: companyQuery,
        });

    const handleClick = (event) => {
        event.currentTarget.focus();
        event.currentTarget.scrollIntoView({ block: 'end' });
    };

    const handleClickAway = (event) => {
        event.currentTarget?.blur?.();
    };

    const getOptionSelected = (value: CompanyAutocompleteOption, option: CompanyAutocompleteOption) => {
        if (!value || !value.name) {
            return false;
        }
        return value.name === option.name;
    };

    const handleUpdateFocus = (state: boolean) => {
        setFocused(state);
    };

    const filterOptions = createFilterOptions({
        matchFrom: 'start',
        stringify: (option: CompanyAutocompleteOption) => option.name,
    });

    return (
        <>
            <div onClick={handleClick}>
                <Autocomplete
                    value={value}
                    blurOnSelect={true}
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    loading={loading}
                    onChange={(event, value, reason) => {
                        if (reason === 'clear') {
                            setValue({ id: '', name: '', website: '', industry: '', isValid: false });
                            setOptions([]);
                        }
                        if (reason === 'selectOption' && value && typeof value != 'string') {
                            handleValueChange(value);
                        }
                    }}
                    inputValue={focused ? value.name : visibleValue}
                    onInputChange={(e: React.ChangeEvent<{}>) => {
                        if (!e || !(e.target as HTMLTextAreaElement).value) {
                            return;
                        }
                        const val = {
                            id: '',
                            name: (e.target as HTMLTextAreaElement).value,
                            domain: '',
                            industry: '',
                            isValid: false,
                        };
                        setValue(val);
                        if (!loading) {
                            setOptions([]);
                        }
                    }}
                    options={[value, ...options]}
                    filterOptions={filterOptions}
                    isOptionEqualToValue={getOptionSelected}
                    getOptionLabel={(o) => o.name}
                    filterSelectedOptions
                    openOnFocus
                    renderInput={(params) => {
                        return (
                            <>
                                {focused && (
                                    <TextField
                                        {...params}
                                        focused={focused}
                                        autoFocus={true}
                                        ref={params.InputProps.ref}
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                            endAdornment: (
                                                <React.Fragment>{params.InputProps.endAdornment}</React.Fragment>
                                            ),
                                        }}
                                        onBlur={() => handleUpdateFocus(false)}
                                        style={{ display: focused ? 'inherit' : 'none' }}
                                    />
                                )}
                                {!focused && (
                                    <AutocompleteCompanyOption
                                        {...params}
                                        name={value.name}
                                        website={value.website}
                                        industry={value.industry}
                                        isValid={value.isValid}
                                        setFocused={handleUpdateFocus}
                                    />
                                )}
                            </>
                        );
                    }}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <AutocompleteCompanyOption
                                id={option.id}
                                name={option.name}
                                website={option.website}
                                industry={option.industry}
                                isValid={value.isValid}
                            />
                        </li>
                    )}
                    fullWidth
                />
            </div>
        </>
    );
};
